import axios from "axios";
import React, { createContext, useContext, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";

const GlobalContext = createContext(null);

// Custom hook for using the context
export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("useGlobal must be used within a GlobalProvider");
  }
  return context;
};

export const GlobalProvider = ({ children }) => {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const TOKEN_PREFIX = process.env.REACT_APP_TOKEN;
  const [webData, setWebData] = useState({});
  const history = useHistory();

  const [data, setData] = useState({
    loading: false,
    snack: false,
    snack_msg: "",
    snack_success: false,
    campaign_done: false,
    tutorial: {},
    userData: {},
    themeData: null,
  });

  // Add this inside GlobalProvider, before the `value` object

  // Token getters
  const getAdminToken = useCallback(() => {
    return localStorage.getItem(`${TOKEN_PREFIX}_admin`) || null;
  }, [TOKEN_PREFIX]);

  const getAgentToken = useCallback(() => {
    return localStorage.getItem(`${TOKEN_PREFIX}_agent`) || null;
  }, [TOKEN_PREFIX]);

  const getUserToken = useCallback(() => {
    return localStorage.getItem(`${TOKEN_PREFIX}_user`) || null;
  }, [TOKEN_PREFIX]);

  // Token setters (bonus — useful for login pages)
  const setAdminToken = useCallback(
    (token) => {
      localStorage.setItem(`${TOKEN_PREFIX}_admin`, token);
    },
    [TOKEN_PREFIX],
  );

  const setAgentToken = useCallback(
    (token) => {
      localStorage.setItem(`${TOKEN_PREFIX}_agent`, token);
    },
    [TOKEN_PREFIX],
  );

  async function getWebData(params) {
    const res = await hitAxios({
      path: "/api/admin/site-settings/public",
      post: false,
    });
    if (res.data.success) {
      setWebData(res.data?.data);
    }
  }

  const setUserToken = useCallback(
    (token) => {
      localStorage.setItem(`${TOKEN_PREFIX}_user`, token);
    },
    [TOKEN_PREFIX],
  );

  // Enhanced snackbar function
  const showSnack = useCallback((message, success = true) => {
    setData((prev) => ({
      ...prev,
      snack: true,
      snack_msg: message,
      snack_success: success,
    }));

    setTimeout(() => {
      setData((prev) => ({
        ...prev,
        snack: false,
        snack_msg: "",
      }));
    }, 4000);
  }, []);

  // Set loading state
  const setLoading = useCallback((loading) => {
    setData((prev) => ({ ...prev, loading }));
  }, []);

  // Handle logout — supports admin, user, agent
  const handleLogout = useCallback(
    (role = "user") => {
      try {
        if (role === "admin") {
          localStorage.removeItem(`${TOKEN_PREFIX}_admin`);
          history.push("/admin/login");
        } else if (role === "agent") {
          localStorage.removeItem(`${TOKEN_PREFIX}_agent`);
          history.push("/agent/login");
        } else {
          localStorage.removeItem(`${TOKEN_PREFIX}_user`);
          history.push("/user/login");
        }
      } catch (error) {
        console.error("Error during logout:", error);
      }
    },
    [TOKEN_PREFIX, history],
  );

  // Enhanced axios — now supports admin | agent | user token
  const hitAxios = useCallback(
    async ({
      path,
      obj = {},
      admin = false,
      agent = false, // ← NEW
      post = false,
      showLoading = true,
      showSnackbar = true,
      timeout = 60000,
      headers: customHeaders = {},
    }) => {
      // Determine which token to use
      let tokenKey;
      if (admin) {
        tokenKey = `${TOKEN_PREFIX}_admin`;
      } else if (agent) {
        tokenKey = `${TOKEN_PREFIX}_agent`;
      } else {
        tokenKey = `${TOKEN_PREFIX}_user`;
      }

      const token = localStorage.getItem(tokenKey);

      if (showLoading) setLoading(true);

      try {
        const httpMethod = post ? "POST" : "GET";
        const isFormData = obj instanceof FormData;

        const headers = {
          Authorization: token ? `Bearer ${token}` : undefined,
          ...customHeaders,
        };

        if (!isFormData && !headers["Content-Type"]) {
          headers["Content-Type"] = "application/json";
        }

        const config = {
          method: httpMethod,
          url: `${API_BASE_URL}${path}`,
          headers,
          timeout,
        };

        if (post) config.data = obj;

        const response = await axios(config);

        if (showLoading) setLoading(false);

        if (response?.data?.logout) {
          // Determine which role to logout
          const role = admin ? "admin" : agent ? "agent" : "user";
          handleLogout(role);
          if (showSnackbar)
            showSnack("Session expired. Please login again.", false);
          return { ...response, success: false, logout: true };
        }

        if (showSnackbar) {
          if (!response.data.success && response.data.msg) {
            showSnack(response.data.msg, false);
          } else if (response.data.success && response.data.msg) {
            showSnack(response.data.msg, true);
          }
        }

        return response;
      } catch (error) {
        console.error("API Error:", error);
        if (showLoading) setLoading(false);

        if (error.code === "ECONNABORTED") {
          if (showSnackbar)
            showSnack("Request timed out. Please try again.", false);
          return { success: false, error: "Request timed out" };
        } else if (error.message?.includes("Network Error")) {
          if (showSnackbar)
            showSnack(
              "Network error. Please check your internet connection.",
              false,
            );
          return { success: false, error: "Network error" };
        } else if (error.response?.status === 401) {
          const role = admin ? "admin" : agent ? "agent" : "user";
          handleLogout(role);
          if (showSnackbar)
            showSnack("Session expired. Please login again.", false);
          return { success: false, logout: true };
        }

        const errorMessage =
          error.response?.data?.msg || error.message || "An error occurred";
        if (showSnackbar) showSnack(errorMessage, false);

        return {
          success: false,
          error: errorMessage,
          response: error.response,
        };
      }
    },
    [API_BASE_URL, TOKEN_PREFIX, handleLogout, setLoading, showSnack],
  );

  const themeAPI = React.useMemo(
    () => ({
      listThemes: async () => {
        return await hitAxios({
          path: "/api/theme/list-themes",
          post: false,
          showLoading: false,
        });
      },

      getActiveTheme: async () => {
        const resp = await hitAxios({
          path: "/api/theme/active-theme",
          post: false,
          showLoading: false,
        });
        if (resp?.data?.success) {
          setData((prev) => ({
            ...prev,
            themeData: resp?.data?.data?.themeData,
          }));
        }
        return resp;
      },

      getThemeById: async (id) => {
        return await hitAxios({
          path: `/api/theme/get-theme/${id}`,
          post: false,
          showLoading: false,
        });
      },

      getTheme: async () => {
        const resp = await hitAxios({
          path: "/api/theme/active-theme",
          post: false,
          showLoading: false,
        });
        if (resp?.data?.success) {
          setData((prev) => ({
            ...prev,
            themeData: resp?.data?.data?.themeData,
          }));
        }
        return resp;
      },

      setActiveTheme: async (themeId, admin = false) => {
        return await hitAxios({
          path: "/api/theme/set-active-theme",
          obj: { themeId },
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      createTheme: async (payload, admin = false) => {
        return await hitAxios({
          path: "/api/theme/create-theme",
          obj: payload,
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      updateTheme: async (id, themeData, admin = false) => {
        return await hitAxios({
          path: `/api/theme/update-theme/${id}`,
          obj: { themeData },
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      renameTheme: async (id, payload, admin = false) => {
        return await hitAxios({
          path: `/api/theme/rename-theme/${id}`,
          obj: payload,
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      duplicateTheme: async (id, payload = {}, admin = false) => {
        return await hitAxios({
          path: `/api/theme/duplicate-theme/${id}`,
          obj: payload,
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      deleteTheme: async (id, admin = false) => {
        return await hitAxios({
          path: `/api/theme/delete-theme/${id}`,
          obj: {},
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      exportTheme: (id) => {
        window.open(`${API_BASE_URL}/api/theme/export-theme/${id}`, "_blank");
      },

      importTheme: async (payload, admin = false) => {
        return await hitAxios({
          path: "/api/theme/import-theme",
          obj: payload,
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      resetToDefault: async (admin = false) => {
        return await hitAxios({
          path: "/api/theme/reset-to-default",
          obj: {},
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      // ── LEGACY ────────────────────────────────────────────────────────────────

      updateThemePartial: async (updates, admin = false) => {
        return await hitAxios({
          path: "/api/theme/update-theme-partial",
          obj: updates,
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      updateThemeSection: async (section, updates, admin = false) => {
        return await hitAxios({
          path: `/api/theme/update-theme-section/${section}`,
          obj: updates,
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      updateBrandColors: async (colors, admin = false) => {
        return await hitAxios({
          path: "/api/theme/update-brand-colors",
          obj: colors,
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      updateTypography: async (typography, admin = false) => {
        return await hitAxios({
          path: "/api/theme/update-typography",
          obj: typography,
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      updateComponentStyle: async (component, style, admin = false) => {
        return await hitAxios({
          path: `/api/theme/update-component-style/${component}`,
          obj: style,
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      resetTheme: async (admin = false) => {
        return await hitAxios({
          path: "/api/theme/reset-theme-config",
          obj: {},
          post: true,
          admin: true,
          showLoading: false,
        });
      },

      getBackups: async () => {
        return await hitAxios({
          path: "/api/theme/get-theme-backups",
          post: false,
          showLoading: false,
        });
      },

      restoreBackup: async (filename, admin = false) => {
        return await hitAxios({
          path: `/api/theme/restore-theme-backup/${filename}`,
          obj: {},
          post: true,
          admin: true,
          showLoading: false,
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [hitAxios],
  );

  React.useEffect(() => {
    getWebData();
  }, []);

  const value = {
    data,
    setData,
    hitAxios,
    showSnack,
    setLoading,
    themeAPI,
    // Convenience accessors

    // Token getters
    getAdminToken,
    getAgentToken,
    getUserToken,

    // Token setters
    setAdminToken,
    setAgentToken,
    setUserToken,

    loading: data.loading,
    snack: data.snack,
    snack_msg: data.snack_msg,
    snack_success: data.snack_success,
    userData: data.userData,
    themeData: data.themeData,

    webData: webData,
  };

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
};

// Keep the old export for backward compatibility
export { GlobalContext };
